import { motion, useReducedMotion } from 'motion/react'
import { ArrowUpRight, Linkedin } from 'lucide-react'
import { creator } from '@/config/creator'

const perspectives = [
  {
    subject: 'AI & software creation',
    title: 'From an idea to a shared brief.',
    summary: 'I’m interested in making software creation accessible through AI: describing an idea, collaborating with a team and shaping a product without technical barriers standing in the way.',
  },
  {
    subject: 'Software as art',
    title: 'Logic, shaped with imagination.',
    summary: 'I see software as a creative practice. Discipline and care matter as much as the code itself; the ambition is to make something expressive that remains a genuinely useful tool.',
  },
  {
    subject: 'Creativity & technology',
    title: 'Keep curiosity close.',
    summary: 'Robotics, AOR music and Italian cuisine keep craft and curiosity in my life beyond the screen. They remind me that building with technology is also about rhythm, experimentation and a human point of view.',
  },
]

type FeaturedPost = { title: string; summary?: string; excerpt?: string; url: string; date?: string; subject?: string }

function linkedinUrl(value: unknown): string {
  if (typeof value !== 'string') return ''
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && /(^|\.)linkedin\.com$/.test(url.hostname) ? url.href : ''
  } catch { return '' }
}

export default function FounderThoughts() {
  const reduceMotion = useReducedMotion()
  const configured = (creator as unknown as { featuredLinkedIn?: FeaturedPost[] }).featuredLinkedIn
  const posts = Array.isArray(configured) ? configured.filter(post => post && typeof post.title === 'string' && linkedinUrl(post.url)) : []
  const linkedin = Object.values(creator.socials).map(value => linkedinUrl(typeof value === 'string' ? value : (value as { url?: string } | null)?.url)).find(Boolean)
  const reveal = (delay = 0) => ({
    initial: reduceMotion ? false as const : { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: reduceMotion ? 0 : 0.5, delay: reduceMotion ? 0 : delay },
  })

  return (
    <section id="linkedin" aria-labelledby="thoughts-heading" className="creator-section bg-background text-foreground">
      <div id="thoughts" className="creator-container scroll-mt-28">
        <motion.div {...reveal()} className="flex flex-wrap items-end justify-between gap-8">
          <div><p className="creator-label text-muted-foreground">{posts.length ? 'From my LinkedIn' : 'What I’m thinking about'}</p><h2 id="thoughts-heading" className="creator-display mt-4 text-4xl md:text-6xl">Ideas worth<br /><span className="text-primary">exploring together.</span></h2></div>
          {linkedin && <a href={linkedin} target="_blank" rel="noopener noreferrer" className="creator-button"><Linkedin size={18} aria-hidden="true" />Follow me on LinkedIn <ArrowUpRight size={18} aria-hidden="true" /></a>}
        </motion.div>
        {!posts.length && <motion.p {...reveal()} className="mt-7 max-w-2xl text-lg leading-8 text-muted-foreground">These are thematic perspectives, not published posts: a few subjects that shape how I think about building software.</motion.p>}
        <div className="mt-10 grid grid-cols-12 gap-6">
          <motion.div {...reveal()} className="bento-card col-span-12 overflow-hidden bg-accent md:col-span-4">
            <img src="/images/danilo-costa-small.jpg" alt="Danilo Costa" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            <div className="p-7"><p className="creator-label">A human point of view</p><p className="creator-display mt-4 text-3xl">Curiosity is always an open tab.</p><p className="mt-4 leading-7">AI, creativity and the craft of making something useful.</p></div>
          </motion.div>
          <div className="col-span-12 grid gap-6 md:col-span-8">
            {posts.length ? posts.map((post, index) => (
              <motion.article key={post.url} {...reveal(index * 0.04)} className={`bento-card p-7 md:p-8 ${index % 2 ? 'bg-secondary' : 'bg-primary/10'}`}>
                <p className="creator-label text-muted-foreground">{post.subject || 'Featured LinkedIn post'}</p>
                <h3 className="creator-display mt-3 text-2xl md:text-3xl">{post.title}</h3>
                <p className="mt-4 text-lg leading-8">{post.summary || post.excerpt}</p>
                {post.date && !Number.isNaN(Date.parse(post.date)) && <time dateTime={post.date} className="mt-4 block text-sm text-muted-foreground">{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</time>}
                <a href={linkedinUrl(post.url)} target="_blank" rel="noopener noreferrer" className="editorial-link mt-4 font-semibold">Read on LinkedIn <ArrowUpRight size={18} aria-hidden="true" /></a>
              </motion.article>
            )) : perspectives.map((perspective, index) => (
              <motion.article key={perspective.title} {...reveal(index * 0.04)} className={`bento-card p-7 md:p-8 ${index === 0 ? 'bg-primary/10' : index === 1 ? 'bg-secondary' : 'bg-destructive/10'}`}>
                <p className="creator-label text-muted-foreground">{perspective.subject}</p>
                <h3 className="creator-display mt-3 text-2xl md:text-3xl">{perspective.title}</h3>
                <p className="mt-4 text-lg leading-8">{perspective.summary}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
